import { initAnalytics as cwInitAnalytics } from '@cw-elements/analytics-provider';

const getMetaContent = (name) => {
  const selector = `meta[name="${CSS.escape(name)}"]`;
  return document.querySelector(selector)?.content ?? null;
};

const logInitError = (error) => {
  // Analytics must never interfere with account access.
  console.error('[Analytics] init failed:', error); // eslint-disable-line no-console
};

// Static RUM options, merged with the per-env values read from meta tags. IP
// suppression is NOT set here on purpose: with `consent.enabled: true` the
// provider disables IP shipping itself (CWP071), so it would be redundant.
export const DATADOG_OPTIONS = {
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackUserInteractions: true,
  trackLongTasks: true,
  trackResources: true,
  defaultPrivacyLevel: 'mask-user-input',
  useSecureSessionCookie: true,
};

// The accounts pages fire events on the very click/submit that navigates away
// (sign-in, recover password). The default fetch transport gets cancelled
// mid-request on navigation, dropping those events; beacon transport survives
// the unload instead. Cookies are secure-only. `transport`/`cookieOptions` are
// raw Amplitude SDK init options, so they go under the provider's `options` prop.
// The SDK-level IP suppression above (CWP071) stays forced on regardless:
// `sendIpAddressOnConsent` is a provider prop, not an SDK one.
export const AMPLITUDE_OPTIONS = {
  autocapture: false,
  sendIpAddressOnConsent: true,
  options: {
    transport: 'beacon',
    cookieOptions: { secure: true },
  },
};

// The full RUM configuration is forwarded verbatim to the provider's `datadog`
// block (typed as `@datadog/browser-rum`'s RumInitConfiguration). `service`, `env`
// and `version` are mandatory: the provider logs and skips RUM initialization
// entirely when any of them is blank, taking session replay and every
// `datadogAddAction` down with it.
export const buildDatadogConfig = () => {
  const clientToken = getMetaContent('dd-rum-client-token');
  if (!clientToken) return null;

  return {
    clientToken,
    applicationId: getMetaContent('dd-rum-application-id'),
    site: getMetaContent('dd-rum-site'),
    service: getMetaContent('dd-rum-service'),
    env: getMetaContent('dd-rum-env'),
    version: getMetaContent('dd-rum-version'),
    ...DATADOG_OPTIONS,
  };
};

export const buildAmplitudeConfig = () => {
  const clientId = getMetaContent('amplitude-api-key');
  return clientId ? { clientId, ...AMPLITUDE_OPTIONS } : null;
};

// Only pass the blocks we actually have config for; the provider treats a missing
// block as "don't init that SDK". Consent is always enabled — the provider reads
// the CookieBot `statistics` category (the layout still loads CookieBot's uc.js)
// and stamps every event with the resolved `analytics consent`. That property name
// carries a space, not an underscore, since provider 6.1.1 renamed it.
const buildAnalyticsConfig = () => {
  const amplitude = buildAmplitudeConfig();
  const datadog = buildDatadogConfig();
  if (!amplitude && !datadog) return null;

  return {
    ...(amplitude && { amplitude }),
    ...(datadog && { datadog }),
    consent: { enabled: true },
  };
};

// Amplitude has no built-in unload flush, so a tracked-then-navigate event would
// sit queued until the flush timer — long after the page is gone. Flush only
// once the page is actually hidden (the navigation has committed).
export const shouldFlushOnVisibility = (visibilityState) => visibilityState === 'hidden';

// The flush runs from unload-path event listeners, outside init's error path, so
// a throwing flush would surface as an uncaught error. Analytics stays non-critical.
// `client` is the Amplitude BrowserClient the provider returns from initAnalytics.
export const safeFlush = (client) => {
  try {
    client?.flush();
  } catch (error) {
    console.error('[Analytics] Amplitude flush failed:', error); // eslint-disable-line no-console
  }
};

const flushAmplitudeWhileAlive = (client) => {
  document.addEventListener('visibilitychange', () => {
    if (shouldFlushOnVisibility(document.visibilityState)) safeFlush(client);
  });
  // pagehide also covers Safari and the bfcache, where visibilitychange may not fire.
  window.addEventListener('pagehide', () => safeFlush(client));
};

let markAnalyticsReady;

// The provider drops every call made before init resolves, without buffering, so
// whatever reports on page load waits on this. Resolved in a `finally`: a failed
// init makes the provider a no-op, while a pending promise would strand reports.
// eslint-disable-next-line unicorn/prefer-promise-with-resolvers -- needs Chrome 119
export const analyticsReady = new Promise((resolve) => {
  markAnalyticsReady = resolve;
});

// The provider's initAnalytics is async and returns the Amplitude BrowserClient
// it created. We await it to wire the unload flush to that client, but the caller
// (DOMContentLoaded) leaves it fire-and-forget — page init never blocks on
// analytics, and a rejected init must never break account access.
export const initAnalytics = async () => {
  try {
    const config = buildAnalyticsConfig();
    if (!config) return;

    const client = await cwInitAnalytics(config);
    if (client) flushAmplitudeWhileAlive(client);
  } catch (error) {
    logInitError(error);
  } finally {
    markAnalyticsReady();
  }
};
